import { FC } from "react";
import { bnplConsumerApis } from "@payping/api";
import { Button, Flex, Typography, useMessagedAsync, useMessagedAsyncFn } from "@payping/web-components";
import { Link } from "react-router";
import { useAppTranslation } from "@/utils";
import Loading from "@/pages/loading";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";
import PayGetOrderLayout from "../Layout";

interface ContractProps {
  trackingCode: string;
  afterSuccess: () => void;
}

const Contract: FC<ContractProps> = ({ afterSuccess, trackingCode }) => {
  const contractData = useMessagedAsync(() => bnplConsumerApis.getContractInfo(trackingCode));
  const { t } = useAppTranslation();

  const [signContractState, signContract] = useMessagedAsyncFn(async () => {
    try {
      const res = await bnplConsumerApis.signContract({
        trackingCode,
      });
      afterSuccess();
      return res;
    } catch (e: any) {
      if (e.status === 400) {
        try {
          const parsedError = await e.json();
          throw parsedError;
        } catch (jsonError: any) {
          if (jsonError.status === 400) {
            throw jsonError;
          }
          throw e; // Fallback if parsing JSON fails
        }
      }
      throw e;
    }
  }, [afterSuccess, trackingCode]);

  if (contractData.loading) {
    return (
      <PayGetOrderLayout showHeaders={false}>
        <Loading />
      </PayGetOrderLayout>
    );
  }

  return (
    <PayGetOrderLayout showHeaders>
      <Typography>
        <div className="tw:mx-auto tw:max-w-4xl tw:p-6!">
          <div className="tw:mb-8! tw:text-center">
            <h1 className="tw:text-3xl tw:font-bold">قرارداد فروش اقساطی</h1>
          </div>

          <div className="tw:space-y-6">
            <section>
              <h2 className="tw:text-2xl tw:font-semibold">ماده‌ی 1) طرفین قرارداد:</h2>
              <p>
                <strong>1-1)</strong> شركت / آقای / خانم <strong>{contractData?.value?.merchantName}</strong> به شماره
                ملی
                {contractData?.value?.merchantNationalCode} (مشخصات ثبتی)، به نشانی:
                {contractData?.value?.merchantAddress}، که از این پس در این قرارداد «فروشنده» نـامیـده می‌شود.
              </p>
              <p>
                <strong>2-1)</strong> آقای/خانم <strong>{contractData?.value?.consumerName}</strong> ، فرزند
                {contractData?.value?.consumerFatherName} به شماره ملی {contractData?.value?.consumerNationalCode} ، به
                نشانی:
                {contractData?.value?.consumerAddress} ،که از این پس در این قرارداد «خریدار» نـامیـده می‌شود.
              </p>
              <p className="tw:leading-normal">
                <strong>تبصره‌ی 1)</strong> هویت «خریدار» از طریق اطلاعات اعلامی در زمان تشکیل حساب کاربری نیز بررسی و
                تأیید می‌گردد و لذا «خریدار» موظف است جهت ایجاد حساب کاربری و احراز هویت، کد‌ملی و تاریخ تولد خود را جهت
                استعلام و مطابقت با شماره‌ی تلفن همراه ثبت‌شده و اعلام شده و همچنین در صورت نیاز، علاوه بر موارد مذکور،
                سریال پشت کارت ملی، کد‌پستی، شماره‌ی شبا و غیره خود را جهت تکمیل اطلاعات، احراز هویت، صدور گواهی امضای
                دیجیتال، تدوین قرارداد و انجام هر امری که از نظر «پی‌پینگ» یا «فروشنده» لازم باشد و اقدامات مرتبط با
                انجام تعهدات، را به درستی اعلام و ثبت نماید. این اطلاعات، با حفظ شرایط محرمانگی اطلاعات، جهت احراز هویت
                «خریدار» و سایر موارد فوق‌الذکر استفاده می‌شود.
              </p>
            </section>

            <section>
              <h2 className="tw:text-2xl tw:font-semibold">ماده‌ی 2) موضوع و مدت قرارداد:</h2>
              <p>
                <strong>2-1)</strong> موضوع قرارداد عبارت است خرید کالا یا خدمات توسط «خریدار» از «فروشنده» طبق موارد
                اعلامی در .حساب کاربری و فاکتور دیجیتالی ارائه شده، با استفاده از سرویس «خرید اقساطی» در بستر پلتفرم
                ارائه شده از سوی «پی‌پینگ»، از وبسایت «فروشنده» به آدرس
                <Link to={contractData?.value?.merchantWebSite || ""} target="_blank">
                  {contractData?.value?.merchantWebSite}
                </Link>
                .
              </p>
              <p className="tw:leading-normal">
                <strong>تبصره‌ی 1)</strong> «پی‌پینگ» صرفاً به عنوان تسهیل‌گر و دروازه‌ی ارتباط «فروشنده» با «خریدار»،
                ارائه‌ی کالا و خدمات و دریافت و پرداخت اقساط مورد توافق ایشان بوده و لذا طرفین مطلع و موافق هستند که
                «پی‌پینگ» هیچ مسئولیتی در قبال تعهدات، کالا، محصولات و خدمات ارائه شده توسط «فروشنده» و همچنین تعهدات
                «خریدار»، اقساط تعهد شده، وصول مطالبات و غیره ندارد. لازم به ذکر است که صدور چک یا سایر وثایق و تضامین
                در وجه «پی‌پینگ»، تحویل آن‌ها به «پی‌پینگ» و وجود آن‌ها در ید «پی‌پینگ»، به هیچ‌وجه به معنای مسئولیت
                «پی‌پینگ» در خصوص کالا یا خدمت خریداری شده و نیز بازپرداخت اقساط نبوده و حقی برای «فروشنده» یا «خریدار»
                نسبت به «پی‌پینگ» ایجاد نمی‌نماید.
              </p>
              <p>
                <strong>2-2)</strong> شروع این قرارداد از مورخ {contractData?.value?.shortPersianDateNow} لغایت پرداخت
                آخرین قسط یا وصول آن به هر صورتی، خواهد بود.
              </p>
            </section>

            <section>
              <h2 className="tw:text-2xl tw:font-semibold">ماده‌ی 3) مبلغ قرارداد و نحوه‌ی پرداخت:</h2>
              <p>
                مبلغ قرارداد برابر است با جمع مبالغ مندرج در فاکتور مثبوت در حساب کاربری خریدار، به میزان
                {formatLocalizedNumber({ value: contractData?.value?.totalAmount ?? 0 })} ريال که مورد اطلاع طرفین
                می‌باشد و به شرح ذیل پرداخت خواهد شد:
              </p>
              <p>
                <strong>3-1)</strong> مبلغ {formatLocalizedNumber({ value: contractData?.value?.prePayment ?? 0 })}{" "}
                ريال، به عنوان پیش‌پرداخت خرید انجام گرفته که همزمان با تکمیل سفارش، توسط «خریدار» نقداً پرداخت می‌گردد.
              </p>
              <p>
                <strong>3-2)</strong> مبلغ {formatLocalizedNumber({ value: contractData?.value?.creditAmount ?? 0 })}{" "}
                ريال، الباقی مبلغ خرید که طی
                {contractData?.value?.planInstallmentCount} فقره قسط ماهانه، هر یک به مبلغ
                {formatLocalizedNumber({ value: contractData?.value?.installmentAmount ?? 0 })} ريال، در روز{" "}
                {contractData?.value?.dayOfMonth} هر ماه پرداخت خواهد شد. سررسید اولین قسط، به تاریخ{" "}
                {contractData?.value?.firstInstallmentDueDate} می‌باشد.
              </p>
              <p className="tw:leading-normal">
                <strong>تبصره‌ی 2)</strong> فاکتورهای دیجیتالی مثبوت در حساب کاربری «خریدار» و مندرجات آن‌ها، جزء لاینفک
                این قرارداد محسوب می‌گردند.
              </p>
              <p className="tw:leading-normal">
                <strong>تبصره‌ی 3)</strong> شرایط استرداد و مرجوعی کالا یا خدمت دریافت شده، مطابق با قوانین و مقررات
                جاری کشور و همچنین مقررات مثبوت در بخش قوانین و مقررات وبسایت «فروشنده» می‌باشد و «پی‌پینگ» مسئولیتی در
                قبال آن (از جمله در قبال استرداد کالا و یا استرداد وجوه پرداختی) ندارد و تضامین تحویلی به پی‌پینگ، صرفاً
                پس از تأیید «فروشنده» و با درخواست کتبی وی، قابل استرداد می‌باشد.
              </p>
              <p className="tw:leading-normal">
                <strong>تبصره‌ی 4)</strong>در جهت کنترل و مدیریت ریسک فروش اقساطی، ممکن است که «فروشنده»، یک یا چند فقره
                چک یا سفته (بابت ضمانت دریافت اعتبار خرید اقساطی) از خریدار اخذ نماید و «فروشنده» اختیار دارد تا در صورت
                تخلف «خریدار» از انجام تعهدات قراردادی، نسبت به وصول مطالبات، وجه التزام‌های قراردادی و خسارات مربوطه از
                طریق این تضامین اقدام نماید.
              </p>
            </section>

            <section>
              <h2 className="tw:text-2xl tw:font-semibold">ماده‌ی 4) شروط ضمن قرارداد و تعهدات طرفین:</h2>
              <p>
                <strong>4-1)</strong> «فروشنده» متعهد است تا کالای خریداری شده توسط خریدار را پس از دریافت مبالغ نقدی و
                هزینه‌های اعلامی و همچنین انجام تعهدات «خریدار» (از قبیل صدور و تحویل تضامین اقساط)، به صورت کامل و صحیح
                و سالم، به طریق مقتضی تحویل وی نموده و از هرگونه تأخیر غیر موجه در انجام این تعهد پرهیز نماید.
              </p>
              <p>
                <strong>4-2)</strong>«خریدار» مکلف است تا مطابق توافق انجام گرفته، علاوه بر پرداخت مبالغ پیش‌پرداخت و
                هزینه‌های اعلام شده از سوی «فروشنده»، ضمن تنظیم و امضاء قراردادهای مربوطه، تضامین و وثایق مورد توافق را
                طبق موارد اعلامی، صادر و حسب مورد، تحویل «پی‌پینگ» یا «فروشنده» نماید. بدیهیست که نهایی گشتن فروش، منوط
                به انجام کامل مراحل فوق و تحویل وثایق است.
              </p>
              <p>
                <strong>4-3)</strong> در صورت عدم پرداخت هریک از اقساط مورد توافق در سررسید مقرر و گذشت مدت 10 روز از
                تاریخ سررسید و عدم پرداخت در این مدت، کلیه‌ی دیون مؤجل و اقساط آینده‌ی «خریدار»، تبدیل به دین حال گشته و
                لذا «فروشنده» و «پی‌پینگ»، حسب مورد به صورت مجتمعاً و یا منفرداً، حق دارند تا نسبت به وصول دیون پرداخت
                نشده توسط «خریدار» از هر طریق مقتضی (اعم از وثایق، چک، سفته، قراردادها و غیره) اقدام نمایند. بدیهیست که
                پرداخت هرگونه هزینه‌ی مترتبه بر اقدامات لازم جهت وصول مطالبات بر عهده‌ی «خریدار» (بدهکار) بوده و همچنین
                خسارات ناشی از تأخیر در تأدیه‌ی دیون نیز بر ایشان بار می‌گردد.
              </p>
              <p className="tw:leading-normal">
                <strong>تبصره‌ی 1)</strong> در صورت تخلف «خریدار»، هزینه‌های مترتبه بر اقدامات لازم جهت وصول مطالبات بر
                عهده‌ی «خریدار» خواهد بود.
              </p>
              <p>
                <strong>4-4)</strong> وثایق و اسناد تجاری صادره بابت این قرارداد، پس از پرداخت کامل اقساط و تسویه‌ی
                تمامی حساب‌ها و بدهی‌های «خریدار»، به ایشان مسترد خواهد شد و چنان‌چه این اسناد و وثایق نزد «پی‌پینگ»
                باشد، صرفاً با درخواست و اعلام کتبی «فروشنده»، قابل استرداد توسط «پی‌پینگ» خواهند بود.
              </p>
              <p>
                <strong>4-5)</strong> طرفین با امضاء این قرارداد، مطلع گردیدند و توافق نمودند که با توجه به اینکه وظیفه
                و نقش «پی‌پینگ»، صرفاً تسهیل‌گیری در خرید و فروش کالا و دریافت و پرداخت اقساطی است و «فروشنده» با
                استفاده از این پلتفرم، کالا یا خدمات خود را با شرایط اقساطی به «خریداران» ارائه می‌نماید و تعهد
                «پی‌پینگ»، صرفاً تسهیل‌گری در ارائه و دریافت تسهیلات با هدف فروش اقساطی توسط «فروشنده» و خرید اقساطی
                توسط «خریدار» می‌باشد، لذا لازم به توجه است که:
              </p>
              <p className="tw:leading-normal">
                <strong>4-5-1)</strong> پی‌پینگ به هیچ عنوان اطلاعی از نوع خرید «خریدار» از «فروشنده» و کالا یا خدمات
                ارائه شده، ندارد و مسئولیت آن تماماً با «فروشنده و خریدار» است. همچنین مسئولیت هر گونه مغایرت در ارائه‌ی
                کالا یا خدمات، تحویل به موقع کالا یا خدمات، اصالت و گارانتی کالا و پرداخت به موقع پیش‌پرداخت و اقساط، بر
                عهده‌ی «فروشنده» و «خریدار» می‌باشد و «پی‌پینگ» هیچ‌گونه تعهد و مسئولیتی اعم از حقوقی، جزائی و مالی،
                نسبت به این موارد نخواهند داشت؛
              </p>
              <p className="tw:leading-normal">
                <strong>4-5-2)</strong> روابط قراردادی و غیرقراردادی «فروشنده» با «خریدار» که خارج از موضوع و اطلاعات
                این قرارداد باشد، از قبیل تراکنش مالی و تخفیف یا اضافه پرداخت و غیره، ارتباطی با «پی‌پینگ» ندارد؛
              </p>
              <p className="tw:leading-normal">
                <strong>4-5-3)</strong> نظر به عدم اطلاع «پی‌پینگ» از فرآیند خرید و فروش و روابط میان فروشنده و خریدار،
                پی‌پینگ به هیچ عنوان تعهد و مسئولیتی در خصوص وصول مطالبات، پرداخت اقساط و سایر موارد از این دست و به طور
                کلی هر امر دیگر ناشی از ارائه و یا فروش کالا و خدمات توسط فروشنده به خریدار، ندارد؛
              </p>
              <p className="tw:leading-normal">
                <strong>4-5-4)</strong> بدیهی است در صورت تأخیر در پرداخت اقساط و یا عدم پرداخت اقساط و مطالبات توسط
                خریدار،‌ عدم دسترسی به خریدار،‌ وقوع قوه‌ی قاهره از قبیل سیل، زلزله،‌ جنگ، شیوع بیماری و غیره و مانند
                آن، «فروشنده» و «خریدار» حق مطالبه‌ی هیچ‌گونه خسارت احتمالی از پی‌پینگ را ندارند؛
              </p>
              <p className="tw:leading-normal">
                <strong>4-5-5)</strong> چنان‌چه به هر علتی (اعم از الزامات و محدودیت‌های سیستمی در صدور، درخواست فروشنده
                و یا هر علت دیگر) تضامین دریافتی از خریداران، به نام «پی‌پینگ» (شرکت ماناتدبیرآواتک) صادر و در سیستم
                بانکی ثبت گردند، به هیچ‌وجه و به هیچ میزانی موجب مسئولیت «پی‌پینگ» در قبال بازپرداخت اقساط نخواهد بود.
                لیکن با توجه به ثبت تضامین به نام «شرکت ماناتدبیرآواتک»، در صورت عدم بازپرداخت اقساط توسط خریدار و
                درخواست کتبی «فروشنده»، «پی‌پینگ» همکاری لازمه جهت اجرای تضامین و وصول مطالبات «فروشنده» از خریدار یا
                ضامن را خواهد نمود.
              </p>
              <p>
                <strong>4-6)</strong> استرداد کالا براساس قوانین تجارت الکترونیک و توافقات احتمالی با خریدار است و
                «فروشنده» متعهد به رعایت این قوانین و توافقات می‌باشد. همچنین درصورتی‌که بازگشت، مرجوعی یا استرداد سفارش
                اقساطی، طبق قانون انجام گیرد، «فروشنده» موظف است مبلغ نقدی پرداخت شده توسط کاربر که متعلق به این خرید
                است را به حساب بانکی «خریدار» بازگشت داده و تضامین احتمالی دریافت شده از خریدار (اعم از چک یا سفته) را
                حسب مورد مسترد یا از اعتبار ساقط نماید.
              </p>
              <p className="tw:leading-normal">
                <strong>4-7)</strong> اعتبار تخصیص یافته در خرید اقساطی، صرفاً قابل استفاده در سفارش کالا و فروش اقساطی
                یا اعتباری کالا است و قابلیت واریز نقدی به حساب بانکی «خریدار» (تحت هیچ عنوان و با هیچ روشی) را نخواهد
                داشت. همچنین در صورت لغو سفارش خرید، مبلغ اعتبار تخصیص داده شده، قابلیت پرداخت نقدی یا اعتباری به خریدار
                را ندارد و «خریدار»، حق طرح هرگونه ادعا یا اعتراضی در این خصوص را از خود سلب و ساقط نمود.
              </p>
              {/* بند 4-8 */}
              <p>
                <strong>4-8)</strong> محاسبه‌ی اقساط و زمان تودیع آن‌ها از لحظه‌ی خرید شروع شده و مطابق با تاریخ‌های
                اعلام شده در ماده‌ی «3» این قرارداد خواهد بود و ارتباطی با زمان تحویل سفارش به «خریدار» ندارد.
              </p>
              {/* بند 4-9 */}
              <p>
                <strong>4-9)</strong> كليه‌ی تذکرات و قوانین مندرج در قسمت قوانين و مقررات وبسايت «پی‌پینگ» به آدرس
                <Link to="https://payping.ir/" target="_blank">
                  https://payping.ir
                </Link>
                ، و همچنین قوانین و مقرراتی که هنگام ایجاد حساب کاربری «پی‌پینگ» ارائه داده می‌شوند و نیز قوانین و
                مقررات مندرج در وبسایت «فروشنده» به آدرس:
                <Link to={contractData?.value?.merchantWebSite || ""} target="_blank">
                  {contractData?.value?.merchantWebSite}
                </Link>
                جزئي از اين قرارداد تلقي شده و «طرفین» متعهد به رعایت آن‌ها همچون مفاد این قرارداد می‌باشد. همچنین در
                صورت بروز تغییر در قوانین یا ملاحظات «پی‌پینگ»، قوانین جدید و تغییرات یافته، در این قرارداد جاری
                می‌باشند.
              </p>
            </section>
          </div>
          <Flex justify="center" className="tw:w-full" gap={"middle"}>
            <Button loading={signContractState.loading} onClick={signContract} type="primary" className="tw:w-full">
              {t("pay.getOrder.steps.signContract.title")}
            </Button>
          </Flex>
        </div>
      </Typography>
    </PayGetOrderLayout>
  );
};

export default Contract;
